import './tabazienda.css';
import React, { useState, useEffect, useMemo } from "react";
import { Button, Form, Row, Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BsTrash, BsPencil, BsPlusCircle, BsInfoCircle } from "react-icons/bs";
import BuildingDetailModal from "./BuildingDetailModal";
import BuildingInfoModal from "./BuildingInfoModal";
import { useToast } from "../ToastContext/ToastContext";
import { ServiceFactory } from "../../Api/Services/ServiceFactory";
import { ServiceNames } from "../../Api/registry/Services";
import { useDispatch } from "react-redux";
import { fetchPerson } from "../../store/slices/personSlice";
import { useSelector } from 'react-redux';

const FarmDetailSection = ({ farm, onCancel }) => {
  const { t } = useTranslation(["usersetting"]);
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const [farmData, setFarmData] = useState(
    farm || {
      name: "",
      alternateName: "",
      description: "",
      buildings: [],
      parcels: [],
    }
  );
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [showBuildingInfoModal, setShowBuildingInfoModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [userService, setUserService] = useState(null);

  // Otteniamo le parcelle dal PersonDto nello store
  const personData = useSelector((state) => state.person?.person);
  const parcelList = personData?.parcels || [];

  // Costruisci una mappa parcelId -> parcel completo
  const parcelMap = useMemo(() => {
    const map = new Map();
    function addCompleteParcelToMap(parcelList, parcelId) {
      for (const parcel of parcelList || []) {
        if (parcel.id && parcel.name && parcelId === parcel.id) {
          map.set(parcel.id, parcel);
          return;
        }
      }
    }

    for (const parcel of farmData?.parcels || []) {
      if (!parcel.name) {
        addCompleteParcelToMap(parcelList, parcel);
      }
    }
    return map;
  }, [personData, farmData]);

  // Costruisci una mappa cropReferenceId -> cropReference completo
  const cropReferenceMap = useMemo(() => {
    const map = new Map();
    function addOperationsFromParcels(parcelList) {
      for (const parcel of parcelList || []) {
        for (const op of parcel.parcelOperations || []) {
          if (op.cropReference && op.cropReference.id && op.cropReference.name) {
            map.set(op.cropReference.id, op.cropReference);
          }
        }
      }
    }
    // Da tutte le farm
    for (const farm of personData?.agriFarms || []) {
      addOperationsFromParcels(farm.parcels);
    }
    // Dalla lista parcels diretta
    addOperationsFromParcels(personData?.parcels);
    return map;
  }, [personData]);

  useEffect(() => {
    const initializeServices = async () => {
      try {
        const service = await ServiceFactory.getService(ServiceNames.UserService);
        setUserService(service);
      } catch (error) {
        showToast(error.message || t("error:wrong"), "error");
      }
    };
    initializeServices();
  }, []);

  useEffect(() => {
    if (farm && farm.hasBuilding) {
      const buildings = Array.isArray(farm.hasBuilding) ? farm.hasBuilding : [farm.hasBuilding];
      setFarmData({
        ...farm,
        buildings: buildings.map(b => ({
          ...b,
          contactPoints: b.contactPoints
        }))
      });
    } else {
      setFarmData({
        name: "",
        alternateName: "",
        description: "",
        buildings: [],
        parcels: []
      });
    }
  }, [farm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFarmData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSaveFarm = async () => {
    try {
      if (!userService) {
        showToast(t("error:servicenotinitialized"), "error");
        return;
      }

      const request = {
        farmId: farmData.id || null,
        description: farmData.description,
        code: farmData.alternateName,
        name: farmData.name,
        sedi: farmData.buildings.map(building => ({
          id: building.id,
          name: building.name,
          type: building.type,
          description: building.description,
          address: building.address,
          surface: building.surface,
          coordinate: building.coordinate,
          contactPoints: building.contactPoints
        }))
      };

      await userService.saveFarm(request);

      // Aggiorniamo il PersonDto nello store usando fetchPerson
      await dispatch(fetchPerson()).unwrap();

      showToast(t("common:messages.success"), "success");
      onCancel(); // Chiudiamo il detail e torniamo alla lista
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handleEditBuilding = (building) => {
    setSelectedBuilding(building);
    setShowBuildingModal(true);
  };

  const handleAddBuilding = () => {
    setSelectedBuilding(null);
    setShowBuildingModal(true);
  };

  const handleCloseBuildingModal = () => {
    setShowBuildingModal(false);
    setSelectedBuilding(null);
  };

  const handleSaveBuilding = (buildingData) => {
    try {
      if (selectedBuilding) {
        const updatedFarmData = {
          ...farmData,
          buildings: farmData.buildings.map((b) =>
            b.id === buildingData.id ? buildingData : b
          ),
        };
        setFarmData(updatedFarmData);
      } else {
        const newBuilding = {
          ...buildingData,
          id: generateUniqueId(),
        };
        const updatedFarmData = {
          ...farmData,
          buildings: [...farmData.buildings, newBuilding],
        };
        setFarmData(updatedFarmData);
      }
      setShowBuildingModal(false);
      showToast(t("common:messages.success"), "success");
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handleDeleteBuilding = (buildingId) => {
    if (window.confirm(t("usersetting:message.confirmdeletebuilding"))) {
      try {
        const updatedBuildings = farmData.buildings.filter(
          (b) => b.id !== buildingId
        );
        setFarmData((prevData) => ({
          ...prevData,
          buildings: updatedBuildings,
        }));
        showToast(t("common:messages.success"), "success");
      } catch (error) {
        showToast(error.message || t("error:wrong"), "error");
      }
    }
  };

  const generateUniqueId = () => {
    const timestamp = new Date().getTime();
    const random = Math.random().toString(36).substring(2, 7);
    return `building-${timestamp}-${random}`;
  };

  const handleShowInfoBuilding = (building) => {
    setSelectedBuilding(building);
    setShowBuildingInfoModal(true);
  }

  const handleCloseInfoBuilding = () => {
    setSelectedBuilding(null);
    setShowBuildingInfoModal(false);
  }

  function getLastCropName(operationsList) {
    if (!operationsList || operationsList.length === 0) {
      return null;
    }
    const sortedOperations = [...operationsList].sort((a, b) => {
      return new Date(b.toDate) - new Date(a.toDate);
    });
    let operation = sortedOperations[0];
    let retValue;
    if (operation && !operation.cropReference?.name) {
      operation = cropReferenceMap.get(operation.cropReference);
      retValue = operation.name;
    }
    return retValue;
  }

  return (
    <>
      <Form>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>{t("usersetting:caption.farmname")}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={farmData?.name || ""}
                onChange={handleChange}
                isInvalid={!farmData?.name}
              />
              <Form.Control.Feedback type="invalid">
                {t("usersetting:messages.namerequired")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                {t("usersetting:caption.farmalternatename")}
              </Form.Label>
              <Form.Control
                type="text"
                name="alternateName"
                value={farmData?.alternateName || ""}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>{t("usersetting:caption.farmdesc")}</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={farmData?.description || ""}
                onChange={handleChange}
                style={{ maxHeight: "200px" }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Tabella per i building */}
        <Row className="mb-3">
          <Col>
            <h4>{t("usersetting:tiles.buildings")}</h4>
            <div className="d-flex justify-content-end mb-2">
              <Button variant="success" onClick={handleAddBuilding}>
                <BsPlusCircle className="me-1" />
                {t("usersetting:button.caption.addbuilding")}
              </Button>
            </div>
            <Table striped bordered hover responsive className="my-farm-table">
              <thead>
                <tr>

                  <th>{t("usersetting:caption.buildingtabledesc")}</th>
                  <th>{t("usersetting:caption.buildingtableaddress")}</th>
                  <th>{t("usersetting:caption.tableoperations")}</th>
                </tr>
              </thead>
              <tbody>
                {farmData?.buildings && farmData.buildings.length > 0 ? (
                  farmData.buildings.map((building) => (
                    <tr key={building.id}>
                      <td>{building.description}</td>
                      <td>
                        {building.address
                          ? `${building.address.street || ""} ${building.address.houseNumber || ""
                          }, ${building.address.zipCode || ""} ${building.address.city || ""
                          } (${building.address.country || ""})`
                          : ""}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          onClick={() => handleShowInfoBuilding(building)}
                        >
                          <BsInfoCircle />
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => handleEditBuilding(building)}
                        >
                          <BsPencil />
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => handleDeleteBuilding(building.id)}
                        >
                          <BsTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      {t("common:messages.noitemfound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        {/* Tabella per le parcelle */}
        <Row className="mb-3">
          <Col>
            <h4>{t("usersetting:tiles.parcels")}</h4>
            <Table striped bordered hover responsive className="my-farm-table">
              <thead>
                <tr>
                  <th>{t("usersetting:caption.parceltablename")}</th>
                  <th>{t("usersetting:caption.parceltablearea")}</th>
                  <th>{t("usersetting:caption.parceltablecultivation")}</th>
                </tr>
              </thead>
              <tbody>
                {farmData?.parcels && farmData.parcels.length > 0 ? (
                  farmData.parcels.map((parcel) => {
                    if (!parcel.name && parcelMap.has(parcel)) {
                      parcel = parcelMap.get(parcel);
                    }
                    let cropDescription = getLastCropName(parcel.parcelOperations);
                    return (
                      <tr key={parcel.id}>
                        <td>{parcel.name}</td>
                        <td>{parcel.area}</td>
                        <td>{cropDescription || ""}</td>
                      </tr>
                    )
                  }
                  )
                ) : (
                  <tr>
                    <td colSpan="3">
                      {t("common:messages.noitemfound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex justify-content-between">
            <Button variant="secondary" onClick={onCancel}>
              {t("common:button.cancel.caption")}
            </Button>
            <Button variant="primary" onClick={handleSaveFarm}>
              {t("common:button.save.caption")}
            </Button>
          </Col>
        </Row>
      </Form>

      {showBuildingModal && (
        <BuildingDetailModal
          show={showBuildingModal}
          handleClose={handleCloseBuildingModal}
          building={selectedBuilding}
          onSave={handleSaveBuilding}
          isNewBuilding={!selectedBuilding}
        />
      )}

      {showBuildingInfoModal && (
        <BuildingInfoModal
          show={showBuildingInfoModal}
          handleClose={() => handleCloseInfoBuilding()}
          building={selectedBuilding}
        />
      )}
    </>
  );
};

export default FarmDetailSection;